import {
  Component,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SkillGroup, SkillSpecializationListItem } from '../../../models/skill-learning.model';
import {
  SKILL_FILL_TASK_STATUS_LABEL_PREFIX,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEVEL_TYPE,
  SKILL_LEVEL_TYPE_LABEL,
  SKILL_SPECIALIZATION_FILL_TASK_STATUS,
  SKILL_SPECIALIZATION_STATUS,
} from '../../../options/skill-learning.option';

@Component({
  selector: 'app-skill-specialization-list',
  templateUrl: './specialization-list.component.html',
  styleUrls: ['./specialization-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSpecializationListComponent implements OnChanges {

  readonly levelTypes = SKILL_LEVEL_TYPE;
  readonly levelTypeLabels = SKILL_LEVEL_TYPE_LABEL;
  readonly levelCounts = SKILL_LEARNING_LEVEL_COUNT;

  @Input() specializations: SkillSpecializationListItem[] = [];
  @Input() availableGroups: SkillGroup[] = [];
  @Input() canCreate: boolean = false;
  @Input() showSourceDepartment = false;
  @Input() pendingDeleteId: string | null = null;
  departmentNames: { [departmentId: string]: string } = {};

  @Output() editRequested = new EventEmitter<string>();
  @Output() deleteRequested = new EventEmitter<SkillSpecializationListItem>();
  @Output() createRequested = new EventEmitter<void>();
  @Output() fillTaskRequested = new EventEmitter<string>();

  trackBySpecializationId(index: number, item: SkillSpecializationListItem): string {
    return item.id;
  }

  onEditClick(item: SkillSpecializationListItem): void {
    if (this.canShowEditButton(item)) {
      this.editRequested.emit(item.id);
    }
  }

  onDeleteClick(item: SkillSpecializationListItem): void {
    if (this.canShowDeleteButton(item)) {
      this.deleteRequested.emit(item);
    }
  }

  onCreateClick(): void {
    this.createRequested.emit();
  }

  onFillTaskClick(event: Event, item: SkillSpecializationListItem): void {
    event.stopPropagation();
    if (this.canShowFillTaskButton(item)) {
      this.fillTaskRequested.emit(item.id);
    }
  }

  canShowFillTaskButton(item: SkillSpecializationListItem): boolean {
    return !!item && !!item.canCreateFillTask;
  }

  canShowEditButton(item: SkillSpecializationListItem): boolean {
    return !!item && (this.hasNonDepartmentEditCapability(item)
      || (!item.probationRequiredByCompany && !!item.canManageDepartmentProbationRequirement));
  }

  canShowStandardEditButton(item: SkillSpecializationListItem): boolean {
    return this.hasNonDepartmentEditCapability(item);
  }

  canShowDepartmentRequirementButton(item: SkillSpecializationListItem): boolean {
    return !!item && !item.probationRequiredByCompany
      && !!item.canManageDepartmentProbationRequirement
      && !this.hasNonDepartmentEditCapability(item);
  }

  canShowDeleteButton(item: SkillSpecializationListItem): boolean {
    return !!item && !!item.canDeactivate;
  }

  hasSourceDepartment(item: SkillSpecializationListItem): boolean {
    return this.showSourceDepartment && !!item;
  }

  ngOnChanges(): void {
    this.departmentNames = {};
    this.availableGroups.forEach(group => this.departmentNames[group.id] = group.name);
  }

  private hasNonDepartmentEditCapability(item: SkillSpecializationListItem): boolean {
    return !!item && (!!item.canEditContent
      || !!item.canManageParticipantGroups
      || !!item.canManageCurators
      || !!item.canChangeLevelStructure
      || !!item.canManageCompanyProbationRequirement
      || !!item.canDeleteCompanyProbationRequirement
      || !!item.canManageFutureHeadRequirement);
  }

  hasProbationMarker(item: SkillSpecializationListItem): boolean {
    return !!item && !!(item.effectiveProbationRequiredLevel || item.companyProbationRequiredLevel);
  }

  probationRequiredLevel(item: SkillSpecializationListItem): number {
    return item && (item.effectiveProbationRequiredLevel || item.companyProbationRequiredLevel)
      || SKILL_LEARNING_LEVEL_COUNT.ONE;
  }

  hasFutureHeadMarker(item: SkillSpecializationListItem): boolean {
    return !!item && !!item.futureHeadRequiredLevel;
  }

  getDisplayStatus(item: SkillSpecializationListItem): string {
    if (item && item.lastFillTask
      && item.lastFillTask.status === SKILL_SPECIALIZATION_FILL_TASK_STATUS.READY_FOR_REVIEW) {
      return SKILL_SPECIALIZATION_FILL_TASK_STATUS.READY_FOR_REVIEW;
    }
    if (!item || !item.status) {
      return SKILL_SPECIALIZATION_STATUS.DRAFT;
    }
    return item.status;
  }

  getStatusClass(item: SkillSpecializationListItem): string {
    return 'spec-list__status--' + this.getDisplayStatus(item);
  }

  getStatusLabelKey(item: SkillSpecializationListItem): string {
    return 'SKILL_LEARNING_ADMIN_STATUS_' + this.getDisplayStatus(item);
  }

  getFillTaskStatusLabelKey(item: SkillSpecializationListItem): string {
    if (!item || !item.lastFillTask || !item.lastFillTask.status) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
    return SKILL_FILL_TASK_STATUS_LABEL_PREFIX + item.lastFillTask.status;
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-list/specialization-list.component.ts